import { DateTime } from "luxon";
import { SchoolClosure } from "model";

/**
 * Returns the minimum start date that can be selected so to not create any
 * School Closure overlaps.
 */
export function getMaxStartDate(params: { closures: Array<SchoolClosure>; end: string }) {
  const { closures, end } = params;

  // Find all the closured that end before the selected end date
  const closuresBeforeEnd = closures.filter(
    (closure) => DateTime.fromISO(closure.end) < DateTime.fromISO(end),
  );

  // Bail if there are no closures that end before the selected end date
  if (closuresBeforeEnd.length === 0) {
    return undefined;
  }

  // Find the latest end date which is the minimum start date
  const maxStartDate = closuresBeforeEnd.reduce((acc, curr) => {
    return DateTime.fromISO(acc) < DateTime.fromISO(curr.end) ? curr.end : acc;
  }, closuresBeforeEnd[0].end);

  return maxStartDate;
}

/**
 * @description
 * Makes sure that the admin will not be able to set overlapping
 * non-school days
 *
 * @param closures The non-school day(s) / closures
 * @param start The start time in UTC format
 *
 */
export function getMaxEndDate(closures: SchoolClosure[], start: string) {
  // get the last end date first
  const lastEndDate = closures.reduce(
    (acc, curr) => (DateTime.fromISO(acc) < DateTime.fromISO(curr.end) ? acc : curr.end),
    "",
  );

  // return early if the start date is later than the last end date
  // This allows the admin to select an end date if in the future
  if (start > lastEndDate) {
    return "";
  }

  const max = closures.reduce((acc, current) => {
    const currentTime = DateTime.fromISO(current.start);
    const startDate = DateTime.fromISO(start);

    // Only consider closures that are after the start day
    if (startDate < currentTime) {
      return DateTime.fromISO(acc) < currentTime ? acc : current.start;
    }

    return acc;
  }, "");

  return max;
}

export function countDays(closures: SchoolClosure[]) {
  return closures.reduce((acc, curr) => {
    const startDt = DateTime.fromISO(curr.start);
    const endDt = DateTime.fromISO(curr.end);
    const totalDays = endDt.diff(startDt, "days").days;

    let weekends = 0;
    let current = startDt;

    for (let i = 0; i < totalDays; i++) {
      if (current.weekday >= 6) {
        weekends++;
      }
      current = current.plus({ days: 1 });
    }

    // offset by one day so luxon includes the entire day
    // TODO: Will have to revisit when we allow admins to set half days
    return acc + Math.floor(totalDays - weekends) + 1;
  }, 0);
}
